export type GameDefinition = {
  id: string;
  name: string;
  kicker: string;
  description: string;
  cover: string;
  banner?: string;
  icon?: string;
  format: string;
  difficulty: string;
  playable: boolean;
  router: "legacy" | "app";
  launchPath: (goalId: string) => string;
};

const comingSoon = (definition: Omit<GameDefinition, "playable" | "router" | "launchPath">): GameDefinition => ({
  ...definition,
  playable: false,
  router: "legacy",
  launchPath: () => "#"
});

export const gameDefinitions: Record<string, GameDefinition> = {
  tour: {
    id: "tour", name: "Tour", kicker: "MAP RUN",
    description: "Cross the city, answer well, and unlock the next stop.",
    cover: "/images/recraft-pop/tour-cover-pop-v2.png",
    banner: "/images/recraft-pop/tour-banner-pop-v1.png",
    icon: "/images/recraft-pop/icons/tour-pop-v1.svg",
    format: "Multi-stop journey", difficulty: "Progressive",
    playable: true, router: "legacy", launchPath: (goalId) => `/tour/goal/${goalId}`
  },
  "guess-the-verb": comingSoon({
    id: "guess-the-verb", name: "Guess the verb", kicker: "CLUE HUNT",
    description: "Discover the verb using as few clues as possible.",
    cover: "/images/recraft-pop/guess-the-verb-cover-pop-v1.png",
    banner: "/images/recraft-pop/guess-the-verb-banner-pop-v1.png", format: "Deduction", difficulty: "Logic"
  }),
  taboo: comingSoon({
    id: "taboo", name: "Taboo", kicker: "SPEAK AROUND IT",
    description: "Describe the word without using the forbidden words.",
    cover: "/images/recraft-pop/taboo-cover-pop-v1.png",
    banner: "/images/recraft-pop/taboo-banner-pop-v1.png", format: "Speaking duel", difficulty: "Teams"
  }),
  cluedo: comingSoon({
    id: "cluedo", name: "Cluedo", kicker: "READ THE EVIDENCE",
    description: "Read the clues and reconstruct the person, action, and moment.",
    cover: "/images/recraft-pop/cluedo-cover-pop-v1.png",
    banner: "/images/recraft-pop/cluedo-banner-pop-v1.png", format: "Mystery", difficulty: "Advanced"
  }),
  "balloon-pop": comingSoon({
    id: "balloon-pop", name: "Pop the right balloon", kicker: "AIM TRUE",
    description: "Pop only the balloon with the correct form.",
    cover: "/images/recraft-pop/balloon-pop-cover-pop-v1.png",
    banner: "/images/recraft-pop/balloon-pop-banner-pop-v1.png", format: "Precision", difficulty: "Fast"
  }),
  "build-a-sentence": comingSoon({
    id: "build-a-sentence", name: "Build a sentence", kicker: "BUILD THE LINE",
    description: "Arrange the blocks and build a grammatically correct sentence.",
    cover: "/images/recraft-pop/build-a-sentence-cover-pop-v1.png",
    banner: "/images/recraft-pop/build-a-sentence-banner-pop-v1.png", format: "Construction", difficulty: "Progressive"
  }),
  "quiz-buzzer": comingSoon({
    id: "quiz-buzzer", name: "Quiz buzzer", kicker: "BEAT THE BUZZER",
    description: "Answer before the others and protect your correct streak.",
    cover: "/images/recraft-pop/quiz-buzzer-cover-pop-v1.png",
    banner: "/images/recraft-pop/quiz-buzzer-banner-pop-v1.png", format: "Live quiz", difficulty: "Competitive"
  }),
  "tense-sort": comingSoon({
    id: "tense-sort", name: "Tense sort", kicker: "SORT THE TIME",
    description: "Move each verb into the column for the correct tense.",
    cover: "/images/recraft-pop/tense-sort-cover-pop-v1.png",
    banner: "/images/recraft-pop/tense-sort-banner-pop-v1.png", format: "Classification", difficulty: "Focus"
  }),
  "grid-select": comingSoon({
    id: "grid-select", name: "Grid select", kicker: "SCAN THE GRID",
    description: "Find all the requested forms without touching the wrong ones.",
    cover: "/images/recraft-pop/grid-select-cover-pop-v1.png",
    banner: "/images/recraft-pop/grid-select-banner-pop-v1.png", format: "Grid", difficulty: "Precision"
  }),
  "word-runner": comingSoon({
    id: "word-runner", name: "Word runner", kicker: "CHOOSE THE GATE",
    description: "Run through the gate that correctly completes the sentence.",
    cover: "/images/recraft-pop/word-runner-cover-pop-v1.png",
    banner: "/images/recraft-pop/word-runner-banner-pop-v1.png", format: "Runner", difficulty: "Reactive"
  }),
  "word-weave": comingSoon({
    id: "word-weave", name: "Word weave", kicker: "WEAVE THE WORDS",
    description: "Weave vocabulary into a pattern of connected crossings.",
    cover: "/images/recraft-pop/word-weave-cover-pop-v1.png",
    banner: "/images/recraft-pop/word-weave-banner-pop-v1.png", format: "Word crossings", difficulty: "Strategic"
  }),
  "bubble-shooter": comingSoon({
    id: "bubble-shooter", name: "Bubble shooter", kicker: "LOCK AND FIRE",
    description: "Aim at the bubble containing the requested verb form.",
    cover: "/images/recraft-pop/bubble-shooter-cover-pop-v1.png",
    banner: "/images/recraft-pop/bubble-shooter-banner-pop-v1.png", format: "Aim", difficulty: "Fast"
  }),
  "true-false": comingSoon({
    id: "true-false", name: "True or false", kicker: "MAKE THE CALL",
    description: "Decide in a few seconds whether the sentence is correct.",
    cover: "/images/recraft-pop/true-false-cover-pop-v1.png",
    banner: "/images/recraft-pop/true-false-banner-pop-v1.png", format: "Quick choice", difficulty: "Reactive"
  }),
  memory: comingSoon({
    id: "memory", name: "Memory", kicker: "MATCH THE PAIR",
    description: "Match each word with its correct form or translation.",
    cover: "/images/recraft-pop/memory-cover-pop-v1.png",
    banner: "/images/recraft-pop/memory-banner-pop-v1.png", format: "Pairs", difficulty: "Memory"
  }),
  scramble: {
    id: "scramble", name: "Scramble", kicker: "UNSCRAMBLE THE STORY",
    description: "Put the fragments in the correct order and rebuild the sentence.",
    cover: "/images/recraft-pop/scramble-cover-pop-v1.png",
    banner: "/images/recraft-pop/scramble-banner-pop-v1.png",
    format: "Word order", difficulty: "Logic",
    playable: true, router: "app", launchPath: (goalId) => `/scramble/goal/${goalId}`
  },
  cloze: {
    id: "cloze", name: "Cloze", kicker: "FILL THE GAP",
    description: "Read a connected story and place the missing words without losing the thread.",
    cover: "/images/recraft-pop/cloze-cover-pop-v1.png",
    banner: "/images/recraft-pop/cloze-banner-pop-v1.png",
    icon: "/images/recraft-pop/icons/cloze-pop-v1.svg",
    format: "Interactive text", difficulty: "Focus",
    playable: true, router: "app", launchPath: (goalId) => `/cloze/goal/${goalId}`
  },
  connect: {
    id: "connect", name: "Connect", kicker: "LINK THE CLUES",
    description: "Connect each word with the sentence that completes it.",
    cover: "/images/recraft-pop/connect-cover-pop-v1.png",
    banner: "/images/recraft-pop/connect-banner-pop-v1.png",
    icon: "/images/recraft-pop/icons/connect-pop-v1.svg",
    format: "Associations", difficulty: "Logic",
    playable: true, router: "app", launchPath: (goalId) => `/connect/goal/${goalId}`
  },
  "say-it-back": comingSoon({
    id: "say-it-back", name: "Say it back", kicker: "HEAR IT. SAY IT.",
    description: "Listen, repeat, and compare your pronunciation with the model.",
    cover: "/images/recraft-pop/say-it-back-cover-pop-v1.png",
    banner: "/images/recraft-pop/say-it-back-banner-pop-v1.png", format: "Voice", difficulty: "Pronunciation"
  })
};

const fallbackGame = comingSoon({
  id: "game", name: "Practice game", kicker: "LANGUAGE RUN",
  description: "A short session to practise the material in your goal.",
  cover: "/images/game-atlas-v2.jpg", format: "Practice", difficulty: "Standard"
});

export function gameFor(activity: string) {
  return gameDefinitions[activity] ?? fallbackGame;
}
